import { pick } from '@jamesgmarks/utilities';
import { UnionOfArrayElements } from '@/shared/types/union-of-array-elements';
import { RsModule } from './types/rs-module';
import { TypedModule } from './types/typed-module';
import { TRemappedActionsModuleReturn, TMappedActionsModuleReturn } from './types/mapped-types';

/** Used internally to this module. Maps using the dictionary strategy. */
const mapActionsForModuleFromMap = <
  TState extends object,
  TModule extends RsModule<TState>,
  TNewKeys extends string[],
  TKeys extends(keyof TypedModule<TState, TModule>['actions'])[],
  TMap extends Record<UnionOfArrayElements<TNewKeys>, UnionOfArrayElements<TKeys>>,
>(
  module_: TypedModule<TState, TModule>,
  actionMap: TMap,
) => {
  const _actionKeys = Object.keys(actionMap) as unknown as TNewKeys;

  const result = _actionKeys.reduce((acc, newKey: keyof typeof actionMap) => {
    const oldKey = actionMap[newKey];
    return {
      ...acc,
      [newKey]: module_.actions[oldKey],
    };
  }, {} as Record<string, any>);

  return result as TRemappedActionsModuleReturn<TState, TModule, TNewKeys, TKeys, TMap>;
};

/** Used internally to this module. Maps using the string array strategy. */
const mapActionsForModuleFromArray = <
  TState extends object,
  TModule extends RsModule<TState>,
  TKeys extends(keyof TypedModule<TState, TModule>['actions'])[]
>(
  module_: TypedModule<TState, TModule>,
  actionKeys: TKeys,
) => {
  const result = pick(module_.actions, actionKeys);

  return result as TMappedActionsModuleReturn<TState, TModule, TKeys>;
};

/**
 * Extracts the actions from a typed module and maps them to `methods` in a Vue component definition.
 *
 * @param module_ The typed module to map actions from.
 * @param actionKeys Which actions to map. Use a string array or remap using a dictionary where the keys
 *                   are the name you want to use and the values are the names originally used in the module.
 */
export function mapModuleActions <
  TState extends object,
  TModule extends RsModule<TState>,
  TNewKeys extends string[],
  TKeys extends(keyof TypedModule<TState, TModule>['actions'])[],
  TMap extends Record<UnionOfArrayElements<TNewKeys>, UnionOfArrayElements<TKeys>>,
>(
  module_: TypedModule<TState, TModule>,
  actionMap: TMap): TRemappedActionsModuleReturn<TState, TModule, TNewKeys, TKeys, TMap>;

export function mapModuleActions <
  TState extends object,
  TModule extends RsModule<TState>,
  TKeys extends(keyof TypedModule<TState, TModule>['actions'])[]
>(
  module_: TypedModule<TState, TModule>,
  actionKeys: TKeys): TMappedActionsModuleReturn<TState, TModule, TKeys>;

export function mapModuleActions <
  TState extends object,
  TModule extends RsModule<TState>,
  TNewKeys extends string[],
  TKeys extends(
    keyof TypedModule<TState, TModule>['actions'])[],
  TMap extends Record<UnionOfArrayElements<TNewKeys>, UnionOfArrayElements<TKeys>>,
>(
  module_: TypedModule<TState, TModule>,
  actionKeys: TMap | TKeys,
) {
  if (Array.isArray(actionKeys)) {
    return mapActionsForModuleFromArray(module_, actionKeys);
  }
  return mapActionsForModuleFromMap(module_, actionKeys);
}
